import { ArchiveOutlined, AssignmentOutlined, UnarchiveOutlined } from "@mui/icons-material";
import { Box, IconButton, IconButtonProps, Stack, Tooltip } from "@mui/material";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useMessageDialogHandlers,
  useMessageDialogState,
} from "../../App/State/Hooks/MessageDialog/useMessageDialog";
import { ISelectedCandidate } from "../../App/State/Slices/MessageDialogSlice";
import { IMessageContainers } from "../../MessageComponents/Enums/MessageEnums";
import { getMessageContainerDisplayName } from "../../MessageComponents/FunctionMethods/MessageFunctions";
import MessageHistoryDialog from "../../MessageComponents/MessageHistory/MessageHistoryDialog";
import { MemoScheduledMessageDialog } from "../../MessageComponents/ScheduledMessages/ScheduledMessageDialog";
import { useGroupProfileContext } from "../State/GroupProfileContext";
import { useGroupCandidateSelectionContext } from "../State/GroupCandidateSelectionContext";
import ArchiveGroupModal from "./ArchiveGroupModal";
import { MessageGroupButton } from "./MessageGroupButton";

export default function GroupButtons() {
  const { group } = useGroupProfileContext();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const { isOpen, containerType } = useMessageDialogState();
  const { handleOpenDialog } = useMessageDialogHandlers();
  const messageHistoryCount = group.sentMessageCount || 0;
  const hasSentMessages = messageHistoryCount > 0;
  const messageScheduledCount = group.scheduledMessageCount || 0;
  const hasScheduledMessages = messageScheduledCount > 0;

  const {
    getters: { selectedCandidates },
  } = useGroupCandidateSelectionContext();
  const candidateIds = useMemo(() => selectedCandidates.map((c) => c.candidateId), [selectedCandidates]);
  const candidateEntities = useMemo(() => {
    return selectedCandidates.reduce(
      (acc, c) => {
        acc[c.candidateId] = {
          candidateId: c.candidateId,
          firstName: c.firstName,
          lastName: c.lastName,
          hasEmail: c.hasEmail,
          hasPhone: c.hasPhone,
          unsubscribed: c.unsubscribed,
          emailAddress: c.emailAddress,
          phoneNumber: c.phoneNumber,
        };
        return acc;
      },
      {} as Record<string, ISelectedCandidate>,
    );
  }, [selectedCandidates]);

  const openDialog = useCallback(() => {
    if (group.isArchived) {
      return;
    }
    handleOpenDialog({
      isGroupTabsOpen: true,
      containerType: IMessageContainers.GroupTextNow,
      groupId: group.id,
      candidateIds,
      candidateEntities,
      dialogTitle: getMessageContainerDisplayName(containerType) + ": " + group.name,
      groupMemberCount: group.memberCount,
    });
  }, [group, handleOpenDialog, candidateIds, candidateEntities, containerType]);

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
        <MessageGroupButton
          totalMembers={group.memberCount}
          selectedCandidates={selectedCandidates}
          isOpen={isOpen}
          disabled={!group.id || group.isArchived}
          onClick={openDialog}
        />
        <MessageHistoryDialog
          groupId={group.id}
          isGroup={true}
          groupName={group.name}
          disableButton={!hasSentMessages}
          countShow={messageHistoryCount}
        />
        <MemoScheduledMessageDialog
          groupId={group.id}
          isGroup={true}
          groupName={group.name}
          disableButton={!hasScheduledMessages}
          countShow={messageScheduledCount}
        />
        <ArchiveGroupButton
          title={group.isArchived ? "Unarchive Group" : "Archive Group"}
          icon={group.isArchived ? <UnarchiveOutlined /> : <ArchiveOutlined />}
          onClick={() => setArchiveModalOpen(true)}
        />
        {!!group.campaignId && <FormButton title="View Group Form" campaignId={group.campaignId} />}
      </Stack>
      <ArchiveGroupModal
        group={group}
        isOpen={archiveModalOpen}
        handleModalClose={() => setArchiveModalOpen(false)}
      />
    </>
  );
}

const ArchiveGroupButton = ({
  title,
  icon,
  onClick,
}: {
  title: string;
  icon: ReactNode;
  onClick: () => void;
}) => {
  const buttonProps = {
    "aria-label": title,
    "data-test": "toggle-archive-group-dialog",
    "aria-controls": "archive-dialog",
    "aria-haspopup": "true",
    onClick,
    sx: { width: "auto" },
  } as IconButtonProps;

  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton {...buttonProps}>{icon}</IconButton>
      </Box>
    </Tooltip>
  );
};

const FormButton = ({ title, campaignId }: { title: string; campaignId: string }) => {
  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton component={Link} to={`/leadcapture/forms/${campaignId}`}>
          <AssignmentOutlined />
        </IconButton>
      </Box>
    </Tooltip>
  );
};
